/**
 * Everything luminary needs from a video source. It mirrors the slice of `HTMLVideoElement` we
 * actually used, so that the rest of the codebase never has to talk to the DOM interface directly
 * and alternative implementations (frame extractors, offline decoders, tests) can be plugged in
 * through {@link VideoFramesProviderFactory}.
 */

/**
 * The pixels of a single decoded frame. Every member is accepted both by
 * `CanvasRenderingContext2D.drawImage` and by `WebGLRenderingContext.texImage2D`.
 */
export type VideoFrameSource =
    | HTMLVideoElement
    | HTMLCanvasElement
    | OffscreenCanvas
    | ImageBitmap
    | VideoFrame;

/**
 * Mirrors the `HTMLMediaElement.HAVE_*` constants so callers can reason about readiness without
 * reaching for the DOM interface.
 */
export enum VideoReadyState {
    HAVE_NOTHING = 0,
    HAVE_METADATA = 1,
    HAVE_CURRENT_DATA = 2,
    HAVE_FUTURE_DATA = 3,
    HAVE_ENOUGH_DATA = 4,
}

export type VideoFramesProviderEvent =
    | 'loadedmetadata'
    | 'canplay'
    | 'canplaythrough'
    | 'seeking'
    | 'seeked'
    | 'play'
    | 'pause'
    | 'error';

export type VideoFramesProviderListener = (event: Event) => void;

export type VideoFramesProviderError = {
    /** The `MediaError` code when a media element does the decoding, otherwise null. */
    code: number | null;
    message: string;
};

export type VideoFramesProviderListenerOptions = {
    once?: boolean;
};

export interface VideoFramesProvider {
    /**
     * The frame at {@link currentTime}, ready to be drawn to a canvas or uploaded to WebGL.
     * Null until the provider has decoded its first frame.
     */
    readonly currentFrame: VideoFrameSource | null;

    /** Intrinsic size of the video, in pixels. Zero until the metadata has loaded. */
    readonly videoWidth: number;
    readonly videoHeight: number;

    /** Length of the video in seconds, or null while it is still unknown. */
    readonly duration: number | null;

    /**
     * Whether the provider has decoded enough of the source to describe it — in practice, whether
     * it can report a {@link duration}. Assets wait on this before treating the video as usable.
     */
    readonly isLoaded: boolean;

    readonly readyState: VideoReadyState;

    /** True between a `currentTime` assignment and the matching `seeked` event. */
    readonly seeking: boolean;

    readonly paused: boolean;

    /**
     * Why the provider stopped loading its source, or null while it is healthy. It can be set
     * after the video has loaded, when a later byte range fails.
     */
    readonly error: VideoFramesProviderError | null;

    /** The url this provider decodes frames from. */
    readonly sourceUrl: string;

    /**
     * The element carrying this video's audio track, or null when the provider has no audio to
     * route (for example an offline frame extractor). Only the audio system should use it.
     */
    readonly audioElement: HTMLMediaElement | null;

    /** Playback position in seconds. Assigning it starts a seek. */
    currentTime: number;

    playbackRate: number;

    muted: boolean;

    /** Label surfaced in devtools to tell several providers of the same video apart. */
    debugName: string;

    /** Start decoding. Mirrors `HTMLMediaElement.load()`. */
    load(): void;

    play(): Promise<void>;

    pause(): void;

    /**
     * An independent provider for the same source, used to give every playback region its own
     * decoder so regions can be pre-seeked in parallel.
     */
    clone(): VideoFramesProvider;

    /** Release every resource held by this provider. It must not be used afterwards. */
    destroy(): void;

    addEventListener(
        type: VideoFramesProviderEvent,
        listener: VideoFramesProviderListener,
        options?: VideoFramesProviderListenerOptions,
    ): void;

    removeEventListener(
        type: VideoFramesProviderEvent,
        listener: VideoFramesProviderListener,
    ): void;
}

export type VideoFramesProviderParams = {
    sourceUrl: string;
    mimeType: string;
};

/**
 * Creates the provider backing a single video asset. Luminary takes one of these as the
 * `createVideoElement` setting; the default builds an {@link HtmlVideoFramesProvider}.
 */
export type VideoFramesProviderFactory = (params: VideoFramesProviderParams) => VideoFramesProvider;
